/**
 * AI CONTEXT
 * Purpose: two-line missed-makeup context for teacher attendance (project, then class + date + week).
 * Inputs: missed session id; subscribes to session, class, and project via useOneDoc so missed docs load outside admin date scope.
 * Used by: TeacherAttendanceSection → AttendanceRow subtitle.
 */
import { Spin } from "antd"
import { useMemo, useState } from "react"

import { classAtoms, classSessionAtoms, projectAtoms } from "#atoms"
import Flex from "#blocks/Flex"
import LinkButton from "#blocks/LinkButton"
import TextOverrun from "#blocks/TextOverrun"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import { type StudentConstructor } from "#customTypes/composedTypes/studentTypes"
import { Uuid } from "#customTypes/globalTypes"
import AddProjectFormModal from "#forms/AddProject/AddProjectFormModal"
import { useOneDoc } from "#hooks/jotai/useSubscription"
import { getSessionInfoFromId } from "#utils/classHelpers"
import { getMissedMakeupSubtitleLines } from "#utils/makeupAttendanceDisplayHelpers"
import { getProjectedProjectDay } from "#utils/studentHelpers"

const MissedMakeupSessionSubtitle = ({
  missedSessionId,
  student,
}: {
  missedSessionId: Uuid
  student: StudentConstructor
}) => {
  const [selectedProjectId, setSelectedProjectId] = useState<Uuid | null>(null)
  const [isProjectHovered, setIsProjectHovered] = useState(false)
  const session = useOneDoc(
    classSessionAtoms,
    DOC_TYPES.CLASS_SESSIONS,
    missedSessionId
  )
  const classDoc = useOneDoc(
    classAtoms,
    DOC_TYPES.CLASSES,
    session?.classId ?? undefined
  )
  const projectId = session?.projectId
  const project = useOneDoc(
    projectAtoms,
    DOC_TYPES.PROJECTS,
    projectId ?? undefined
  )
  const [classId, sessionIndex] = useMemo(
    () => getSessionInfoFromId(missedSessionId),
    [missedSessionId]
  )
  const status = student.attendance?.[classId]?.[sessionIndex]?.status
  const projectedDay = getProjectedProjectDay(
    student,
    session?.projectId,
    status
  )
  const daysToComplete = project ? parseInt(project.daysToComplete, 10) : NaN
  const shouldShowFraction = Number.isFinite(daysToComplete)

  const lines = useMemo(
    () => getMissedMakeupSubtitleLines(classDoc?.name, session, project),
    [classDoc?.name, session, project]
  )

  const isSessionLoading = !session
  const isProjectLoading = Boolean(session && projectId && !project)

  if (isSessionLoading) {
    return (
      <Flex col gap={6} style={{ minWidth: 0, width: "100%" }}>
        <Flex gap={8} center style={{ justifyContent: "flex-start" }}>
          <Spin size="small" />
          <span>Loading session…</span>
        </Flex>
      </Flex>
    )
  }

  if (!lines) return null

  return (
    <Flex col gap={2} style={{ minWidth: 0, width: "100%" }}>
      {isProjectLoading ? (
        <Flex gap={8} center style={{ justifyContent: "flex-start" }}>
          <Spin size="small" />
          <span>Loading project…</span>
        </Flex>
      ) : (
        <Flex
          center
          gap={4}
          style={{
            justifyContent: "flex-start",
            flexWrap: "nowrap",
            minWidth: 0,
            width: "100%",
          }}
        >
          <LinkButton
            onClick={() => {
              if (session.projectId) setSelectedProjectId(session.projectId)
            }}
            style={{
              minWidth: 0,
              color: "inherit",
              textDecoration: isProjectHovered ? "underline" : "none",
            }}
            onMouseEnter={() => setIsProjectHovered(true)}
            onMouseLeave={() => setIsProjectHovered(false)}
          >
            <TextOverrun text={lines.projectName} />
          </LinkButton>
          {shouldShowFraction && (
            <span style={{ flexShrink: 0 }}>
              ({projectedDay}/{daysToComplete})
            </span>
          )}
        </Flex>
      )}
      <TextOverrun text={lines.classInfoLine} />
      {selectedProjectId && (
        <AddProjectFormModal
          projectId={selectedProjectId}
          onClose={() => setSelectedProjectId(null)}
        />
      )}
    </Flex>
  )
}

export default MissedMakeupSessionSubtitle
